import { getValByPath } from "@utils";
import { computed } from "mobx";
import { inject, observer } from "mobx-react";
import { Dropdown } from "primereact/dropdown";
import { Panel } from "primereact/panel";
import { useEffect, useMemo, useState } from "react";
import "./style.scss";
import ButtonUI from "@UI/ButtonUI";

const DeviceTarget = inject("mainStore")(
    observer(({ mainStore, prePath = [], disabled }) => {
        const detailData = computed(() => mainStore.getMainStore("detailData")).get();
        const [root, setRoot] = useState(null);

        const path = prePath.join(".");
        const valueFromPath = getValByPath(path, detailData) || null;

        useEffect(() => {
            if (!root) {
                let obj = defaultData;
                if (valueFromPath) obj = valueFromPath;
                setRoot(obj);
            }
        }, [valueFromPath]);

        const globalState = useMemo(() => computeGlobalState(root), [root]);

        const handleAddCondition = (group, setGroup) => {
            const newCond = { field: "", operator: "$eq", value: "" };
            setGroup({
                ...group,
                conditions: [...(group.conditions || []), newCond],
            });
        };

        const handleAddGroup = (group, setGroup, type) => {
            const newGroup = { type, conditions: [], children: [] };
            setGroup({
                ...group,
                children: [...(group.children || []), newGroup],
            });
        };

        const handleDeleteCondition = (group, setGroup, i) => {
            const newConds = (group.conditions || []).filter((_, idx) => idx !== i);
            setGroup({ ...group, conditions: newConds });
        };

        const handleDeleteGroup = (parentGroup, setParentGroup, i) => {
            const newChildren = (parentGroup.children || []).filter((_, idx) => idx !== i);
            setParentGroup({ ...parentGroup, children: newChildren });
        };

        const handleChangeCondition = (group, setGroup, i, updated) => {
            const newConds = [...(group.conditions || [])];
            newConds[i] = updated;
            setGroup({ ...group, conditions: newConds });
        };

        const Group = ({ group, setGroup, isRoot = false, parentGroup, setParentGroup, index, globalStateProp = {} }) => {
            const label = group.type === "$and" ? "И" : "ИЛИ";

            const { eqCount, neCount } = globalStateProp || {};

            const globalHasEq = (field) => !!(eqCount && eqCount[field] > 0);

            const getAvailableOptions = (currentField) => {
                if (group.type !== "$and") return options;
                return options.filter((opt) => opt.id === currentField || !globalHasEq(opt.id));
            };

            const getAvailableOperators = (field, currentOperator) => {
                if (group.type !== "$and" || !field) return [...operators];

                // Подсчёт количества условий по этому полю
                const totalEq = eqCount?.[field] || 0;
                const totalNe = neCount?.[field] || 0;

                // Исключаем текущее условие, если редактируем его
                const otherEq = totalEq - (currentOperator === "$eq" ? 1 : 0);
                const otherNe = totalNe - (currentOperator === "$ne" ? 1 : 0);

                let filtered = otherEq > 0 || otherNe > 0 ? operators.filter((o) => o.value === "$ne") : [...operators];

                if (currentOperator && !filtered.some((o) => o.value === currentOperator)) {
                    const cur = operators.find((o) => o.value === currentOperator);
                    if (cur) filtered = [...filtered, cur];
                }
                return filtered;
            };

            const getDefaultOperatorForField = (field) => {
                if (!field || group.type !== "$and") return "$eq";

                const totalNe = (neCount && neCount[field]) || 0;
                return totalNe > 0 ? "$ne" : "$eq";
            };

            const disableAddCondition = group.type === "$and" && globalHasEq("deviceType") && globalHasEq("osType");

            const handleSwitchType = () => {
                setGroup({
                    ...group,
                    type: group.type === "$and" ? "$or" : "$and",
                    conditions: [],
                    children: group.children || [],
                });
            };

            return (
                <div className={`tb-group ${isRoot ? "tb-root-group" : ""}`} data-type={group.type}>
                    <div className={`tb-branch-line ${isRoot ? "tb-branch-line__root" : ""}`}>
                        <span className="tb-line-label">{label}</span>
                    </div>

                    <Panel
                        header={
                            <div className="tb-group-header">
                                <div className="tb-group-header-left">
                                    <span className="tb-group-title">Группа ({label})</span>
                                    <ButtonUI label="Сменить тип" size="small" className="p-button-text p-button-secondary tb-switch-btn" onClick={handleSwitchType} disabled={disabled} />
                                </div>

                                <div className="tb-group-buttons">
                                    <ButtonUI label="+ И" size="small" className="p-button-text p-button-sm" onClick={() => handleAddGroup(group, setGroup, "$and")} disabled={disabled} />
                                    <ButtonUI label="+ ИЛИ" size="small" className="p-button-text p-button-sm" onClick={() => handleAddGroup(group, setGroup, "$or")} disabled={disabled} />
                                    <ButtonUI
                                        label="+ Условие"
                                        size="small"
                                        className="p-button-text p-button-sm"
                                        disabled={disableAddCondition || disabled}
                                        onClick={() => handleAddCondition(group, setGroup)}
                                        tooltip={disableAddCondition ? "Нельзя добавить больше условий: deviceType и osType уже зафиксированы" : undefined}
                                    />
                                    {!isRoot && <ButtonUI icon="pi pi-trash" size="small" className="p-button-text p-button-danger p-button-sm" onClick={() => handleDeleteGroup(parentGroup, setParentGroup, index)} disabled={disabled} />}
                                </div>
                            </div>
                        }
                        className={`tb-group-panel ${group?.conditions?.length || group?.children?.length ? "" : "tb-group-panel__hidden"}`}
                    >
                        <div className="tb-group-content">
                            {(group.conditions || []).map((cond, i) => (
                                <div className={`tb-condition ${group.children && group.children.length > 0 && i === group.conditions.length - 1 ? "tb-condition--spaced" : ""}`} key={i}>
                                    <Dropdown
                                        value={cond.field}
                                        options={getAvailableOptions(cond.field)}
                                        optionLabel="label"
                                        optionValue="id"
                                        placeholder="Параметр"
                                        className="tb-dropdown tb-dropdown--wide"
                                        onChange={(e) => {
                                            const newField = e.value;
                                            const newOperator = getDefaultOperatorForField(newField);
                                            handleChangeCondition(group, setGroup, i, {
                                                ...cond,
                                                field: newField,
                                                value: "",
                                                operator: newOperator,
                                            });
                                        }}
                                        disabled={disabled}
                                    />

                                    <Dropdown
                                        value={cond.operator}
                                        options={getAvailableOperators(cond.field, cond.operator)}
                                        optionLabel="label"
                                        optionValue="value"
                                        className="tb-dropdown tb-dropdown--operator"
                                        onChange={(e) => handleChangeCondition(group, setGroup, i, { ...cond, operator: e.value })}
                                        disabled={disabled}
                                    />

                                    <Dropdown
                                        value={cond.value}
                                        options={subOptions[cond.field] ? subOptions[cond.field] : []}
                                        optionLabel="label"
                                        optionValue="id"
                                        placeholder="Значение"
                                        className="tb-dropdown tb-dropdown--wide"
                                        onChange={(e) => handleChangeCondition(group, setGroup, i, { ...cond, value: e.value })}
                                        disabled={disabled}
                                    />

                                    <ButtonUI icon="pi pi-times" size="small" className="p-button-text p-button-danger tb-btn-delete" onClick={() => handleDeleteCondition(group, setGroup, i)} disabled={disabled} />
                                </div>
                            ))}

                            {(group.children || []).map((child, idx) => (
                                <Group
                                    key={idx}
                                    group={child}
                                    setGroup={(updated) => {
                                        const newChildren = [...(group.children || [])];
                                        newChildren[idx] = updated;
                                        setGroup({ ...group, children: newChildren });
                                    }}
                                    parentGroup={group}
                                    setParentGroup={setGroup}
                                    index={idx}
                                    globalStateProp={group.type === "$and" ? computeGlobalState(group) : {}}
                                />
                            ))}
                        </div>
                    </Panel>
                </div>
            );
        };

        return root ? (
            <div className="tb-root">
                <Group
                    group={root}
                    setGroup={(updated) => {
                        setRoot(updated);
                        mainStore.setMainStore("detailData." + path, updated);
                    }}
                    isRoot
                    globalStateProp={globalState}
                />
            </div>
        ) : null;
    }),
);

const computeGlobalState = (node) => {
    const eqCount = {};
    const neCount = {};

    const walk = (n) => {
        if (!n) return;
        const conds = n.conditions || [];
        conds.forEach((c) => {
            if (!c.field) return;
            if (c.operator === "$eq") eqCount[c.field] = (eqCount[c.field] || 0) + 1;
            if (c.operator === "$ne") neCount[c.field] = (neCount[c.field] || 0) + 1;
        });
        (n.children || []).forEach((ch) => walk(ch));
    };

    walk(node);
    return { eqCount, neCount };
};

const options = [
    { label: "Тип устройства пользователя", id: "deviceType" },
    { label: "ОС устройства пользователя", id: "osType" },
];

const subOptions = {
    deviceType: [
        { label: "Mobile", id: "mobile" },
        { label: "Desktop", id: "desktop" },
        // { label: "Tablet", id: "tablet" },
    ],
    osType: [
        { label: "IOS", id: "ios" },
        { label: "Android", id: "android" },
        { label: "Windows", id: "windows" },
        { label: "Linux", id: "linux" },
        { label: "MacOS", id: "macOS" },
    ],
};

const operators = [
    { label: "=", value: "$eq" },
    { label: "≠", value: "$ne" },
];

const defaultData = {
    type: "$or",
    conditions: [],
    children: [],
};

export default DeviceTarget;

// const isValidUserAgent = (root, condition) => {
//     const compileCondition = (condition) => {
//         const { field, operator, value } = condition;
//         switch (operator) {
//             case "$eq":
//                 return (agent) => agent[field].toLowerCase() === value.toLowerCase();
//             case "$ne":
//                 return (agent) => agent[field].toLowerCase() !== value.toLowerCase();
//             default:
//                 return () => false;
//         }
//     };

//     const compileNode = (node) => {
//         const isOr = node.type === "$or";
//         const isAnd = node.type === "$and";
//         const conditionFns = (node.conditions || []).map(compileCondition);
//         const childFns = (node.children || []).map(compileNode);

//         return (agent) => {
//             if (isAnd) {
//                 for (const fn of conditionFns) if (!fn(agent)) return false;
//                 for (const fn of childFns) if (!fn(agent)) return false;
//                 return true;
//             } else if (isOr) {
//                 for (const fn of conditionFns) if (fn(agent)) return true;
//                 for (const fn of childFns) if (fn(agent)) return true;
//                 return false;
//             } else {
//                 return false;
//             }
//         };
//     };

//     return compileNode(root)(condition);
// };
